import React, { useState } from 'react';
import {
    Calculator, IndianRupee, CreditCard, Smartphone,
    RefreshCcw, TrendingUp, Wallet
} from 'lucide-react';

const IncomeCalculator = () => {
   
    const [inputs, setInputs] = useState({
        panCount: "",
        atmVolume: "",
        bbpsCount: ""
    });

    
    const PAN_RATE = 5;         
    const ATM_RATE = 0.002;     
    const BBPS_RATE = 2;        

 
    const panIncome = Number(inputs.panCount) * PAN_RATE;
    const atmIncome = Number(inputs.atmVolume) * ATM_RATE;
    const bbpsIncome = Number(inputs.bbpsCount) * BBPS_RATE;

    const dailyTotal = panIncome + atmIncome + bbpsIncome;
    const monthlyTotal = dailyTotal * 30;

    const handleInputChange = (e) => {
        const { name, value } = e.target;
      
        if (value === "" || /^[0-9\b]+$/.test(value)) {
            setInputs(prev => ({ ...prev, [name]: value }));
        }
    };

    const reset = () => setInputs({ panCount: "", atmVolume: "", bbpsCount: "" });

    return (
        <div className="min-h-screen bg-[#F8FAFC] py-16 px-6">
            <div className="max-w-6xl mx-auto">

               
                <div className="text-center mb-12">
                    <h2 className="text-3xl md:text-5xl font-spacegrotesk font-bold  text-slate-900 mb-4">
                        Income <span className="text-blue-600">Calculator</span>
                    </h2>
                    <p className="text-slate-500 text-lg">Enter your expected daily business volume to see your potential earnings.</p>
                </div>

                <div className="grid lg:grid-cols-12 gap-8 items-start">

                    
                    <div className="lg:col-span-7 bg-white rounded-[2.5rem] p-8 md:p-12 shadow-xl shadow-slate-200/50 border border-slate-100">
                        <div className="flex justify-between items-center mb-10">
                            <h3 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                                <Calculator className="text-blue-600" /> Service Turnover
                            </h3>
                            <button
                                onClick={reset}
                                className="text-xs font-bold text-slate-400 hover:text-red-500 flex items-center gap-1 transition-colors uppercase tracking-widest"
                            >
                                <RefreshCcw size={14} /> Reset
                            </button>
                        </div>

                        <div className="space-y-8">
                           
                            <div className="group">
                                <label className="block text-sm font-bold text-slate-700 mb-3 ml-1">No. of PAN Cards (Daily)</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                        <CreditCard size={20} className="text-slate-400 group-focus-within:text-blue-500 transition-colors" />
                                    </div>
                                    <input
                                        type="text"
                                        name="panCount"
                                        placeholder="Enter Value"
                                        value={inputs.panCount}
                                        onChange={handleInputChange}
                                        className="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl py-4 pl-12 pr-4 focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all font-semibold text-slate-900"
                                    />
                                </div>
                            </div>

                           
                            <div className="group">
                                <label className="block text-sm font-bold text-slate-700 mb-3 ml-1">Micro ATM Volume (Daily ₹)</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                        <IndianRupee size={20} className="text-slate-400 group-focus-within:text-emerald-500 transition-colors" />
                                    </div>
                                    <input
                                        type="text"
                                        name="atmVolume"
                                        placeholder="Enter Value"
                                        value={inputs.atmVolume}
                                        onChange={handleInputChange}
                                        className="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl py-4 pl-12 pr-4 focus:bg-white focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 outline-none transition-all font-semibold text-slate-900"
                                    />
                                </div>
                            </div>

                            
                            <div className="group">
                                <label className="block text-sm font-bold text-slate-700 mb-3 ml-1">No. of BBPS Bills (Daily)</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                        <Smartphone size={20} className="text-slate-400 group-focus-within:text-purple-500 transition-colors" />
                                    </div>
                                    <input
                                        type="text"
                                        name="bbpsCount"
                                        placeholder="Enter Value"
                                        value={inputs.bbpsCount}
                                        onChange={handleInputChange}
                                        className="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl py-4 pl-12 pr-4 focus:bg-white focus:border-purple-500 focus:ring-4 focus:ring-purple-500/10 outline-none transition-all font-semibold text-slate-900"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                   
                    <div className="lg:col-span-5 sticky top-10">
                        <div className="bg-slate-900 rounded-[2.5rem] p-8 md:p-10 text-white shadow-2xl shadow-blue-900/30 overflow-hidden relative">
                            <div className="absolute -right-10 -top-10 w-40 h-40 bg-blue-600 opacity-20 rounded-full blur-3xl"></div>

                            <div className="relative z-10">
                                <p className="text-blue-400 font-bold text-xs uppercase tracking-[0.2em] mb-8 flex items-center gap-2">
                                    <TrendingUp size={16} /> Estimated Earnings
                                </p>

                                <div className="space-y-8">
                                    <div>
                                        <span className="text-slate-400 text-sm block mb-1 font-medium">Daily Income</span>
                                        <div className="flex items-baseline gap-2">
                                            <span className="text-5xl font-black text-white">₹{dailyTotal.toLocaleString()}</span>
                                        </div>
                                    </div>

                                    <div className="p-6 bg-white/5 rounded-[2rem] border border-white/10">
                                        <div className="flex justify-between items-center mb-4">
                                            <span className="text-slate-400 text-sm">Monthly Total</span>
                                            <span className="text-2xl font-bold text-emerald-400">₹{monthlyTotal.toLocaleString()}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-slate-400 text-sm">Annual Potential</span>
                                            <span className="text-slate-200 font-semibold tracking-wide">₹{(dailyTotal * 365).toLocaleString()}</span>
                                        </div>
                                    </div>

                                    <div className="space-y-3">
                                       
                                        <p className="text-center text-[12px] text-slate-500 px-4">
                                            *This is approx calculation based on standard commission rates and can vary.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        
                        <div className="mt-6 p-6 bg-white rounded-3xl border border-slate-200 shadow-sm flex items-center gap-4">
                            <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600">
                                <Wallet size={24} />
                            </div>
                            <div>
                                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Passive Income</p>
                                <p className="text-sm font-medium text-slate-700 italic">"Turn your shop into a digital bank."</p>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default IncomeCalculator;